import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Component, OnInit, DoCheck } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ColorPickerComponent } from 'ngx-color-picker';
import { Feature } from 'ol';
import { Color } from 'ol/color';
import { ColorLike } from 'ol/colorlike';
import { Fill, Stroke, Style } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { StyleFunction } from 'ol/style/Style';

import { GlobalSelectionControlService } from 'src/app/map/services/global-selection-control.service';
import { Tab } from 'src/app/models/Tab';
import { CoverageQuantitativeAssessmentComponent } from 'src/app/popup/calculations/coverage-quantitative-assessment/coverage-quantitative-assessment.component';
import { ChangeTransparencyComponent } from 'src/app/popup/change-transparency/change-transparency.component';
import { CoverageColorPickerComponent } from 'src/app/popup/coverage-color-picker/coverage-color-picker.component';
import { AnalysisToolService } from 'src/app/services/analysis-tool.service';
import { CalculationsService } from 'src/app/services/calculations.service';
import { ExportDataService } from 'src/app/services/export-data';
import { MapService } from 'src/app/services/map.service';
import { ModalConfirmationService } from 'src/app/services/modal-confirmation.service';
import { ModalControlService } from 'src/app/services/modal-control.service';
import { PointsService } from 'src/app/services/points.service';
import { TabsService } from 'src/app/services/tabsService';
import { ToolsControlService } from 'src/app/services/tools-control.service';
import { UserSettingsService } from 'src/app/services/userSettings.service';
import { RGBColor, UtilsService } from 'src/app/services/utils.service';
import { SingleTextInputModalComponent } from 'src/app/utils/modals/single-text-input-modal/single-text-input-modal.component';
import { MenuManagementService } from '../services/menu-management.service';
import { Layer } from '../view-menu/layer';
import { TopLevelLayers } from '../view-menu/top-level-layers';
import { UiBlockService } from 'src/app/services/ui-block.service';
import { ProgressService } from 'src/app/services/progress.service';
import { HorizonToolService } from 'src/app/services/horizon-tool.service';
import { ProjectService } from 'src/app/services/project.service';
import {GritInterferenceTestService} from "../../services/grit/grit.interference-test.service";

@Component({
  selector: 'app-calculation-results',
  templateUrl: './calculation-results.component.html',
  styleUrls: ['./calculation-results.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false
})
export class CalculationResultsComponent implements OnInit {
  cancelCalculation(calculationId: string) {
    this.modalConfirmation.showMesssageConfirmationModal(
      'Cancel Calculation',
      'Are you sure you want to cancel this calculation?',
      'Yes',
      'No',
      () => {
        this.calculationsService.cancelCalculation(calculationId).subscribe(
          () => {
            this.modalService.showMesssageModal('Cancel Calculation', 'Calculation cancelled successfully.');
          },
          (error) => {
            this.modalService.showMesssageModal('Cancel Calculation', 'Failed to cancel calculation.');
          }
        );
      }
    );
  }

  isDeleteAll: boolean = false;


  constructor(
    public menuService: MenuManagementService,
    public globalSelectionService : GlobalSelectionControlService,
    public pointsService : PointsService,
    public calculationsService : CalculationsService,
    private popupService : BsModalService,
    private modalConfirmation: ModalConfirmationService,
    public toolService : ToolsControlService,
    public tabsService: TabsService,
    public utilsService: UtilsService,
    public mapService : MapService,
    private modalService: ModalControlService,
    public userSettingsService: UserSettingsService,
    private cdRef: ChangeDetectorRef,
    public exportDataService: ExportDataService,
    public analysisToolService: AnalysisToolService,
    private uiBlock : UiBlockService,
    public progressService: ProgressService,
    public horizonToolService: HorizonToolService,
    public projectService: ProjectService,
    public interferenceTestService: GritInterferenceTestService

  ) {
    this.progressService.cdRefCalcResultsMenu = cdRef; // used for change detection on progress messages. Triggered by progress service
    }


  ngOnInit(): void {
  }
  ngDoCheck(){

  }

  getCalculations() : TopLevelLayers[]{
    return this.globalSelectionService.calculationLayers.childLayers.filter(layer => layer.calcID != -1);
  }

  openCQA(calcID:number){
    let calculation: TopLevelLayers =  this.calculationsService.getCalculationFromMap(calcID);
    let bsModalRef : BsModalRef = this.popupService.show(CoverageQuantitativeAssessmentComponent);
    bsModalRef.content.elevation_info = [...calculation.elevation_info.keys()];
    bsModalRef.content.id = calcID;
    bsModalRef.content.layerName = 'Redundancy';

  }
  changeCoverageColor(child: TopLevelLayers){

    let bsModalRef : BsModalRef= this.popupService.show(CoverageColorPickerComponent);
    bsModalRef.content.coverageColor = this.utilsService.rgbaStringToHex(child.elevation_info.entries().next().value[1]);
    bsModalRef.content.onClose.subscribe(result=>{
      let calc_settings = this.userSettingsService.getCalculationSettings(child.calcID);
      let [r,g, b] = this.utilsService.extractHexValuesFromString(result);
      let newColor = `rgba(${r},${g},${b},${calc_settings.transparency})`
      this.userSettingsService.setTransparencyCalculation(child.calcID,calc_settings.transparency, newColor);
      let toAdd: number = Math.round((255 / child.elevation_info.size) * 100) / 100;

      child.elevation_info.forEach((color,elevation) =>{
        let newColor = `rgba(${r},${g},${b},${calc_settings.transparency})`;
        child.elevation_info.set(elevation,newColor);
        g = g + toAdd;
        r = r + toAdd;
      })

      child.layers[0].olLayer.getSource().getFeatures()
      .forEach((feature:Feature) =>{
        child.layers[0].olLayer.getSource().removeFeature(feature);

        feature.setStyle( new Style({
          stroke: new Stroke({
            color : child.elevation_info.get(feature.get('ELEV'))
          })
        }));

        child.layers[0].olLayer.getSource().addFeature(feature);
      })
    })
  }
  changeTransparency(child: TopLevelLayers){
    this.changeTransparencyOfLayers(child.calcID,child.layers);
  }
  deleteHorizonToolResultAndCloseTab(){
    let i =this.tabsService.tabs.findIndex(tab => tab.type == TabsService.HORIZON_TOOL)
    if(i != -1){
      this.tabsService.closeTab(i);
    }
    else{
      this.horizonToolService.cancelWindow();
    }
  }
  changeTransparencyOfLayers(calcID:number, layers:Layer[], child?: any, sublayers?:boolean){
    const bsModalRef: BsModalRef = this.popupService.show(ChangeTransparencyComponent);
    if(sublayers == null)
      bsModalRef.content.transparency = this.userSettingsService.getCalculationTransparency(calcID);
    else{
      bsModalRef.content.transparency = this.userSettingsService.getCalculationSettings(calcID).transparency_sub_layers[layers[0].name]
    }

    bsModalRef.content.onClose.subscribe((transparency:number)=>
    {
      if(sublayers == null)
        this.userSettingsService.setTransparencyCalculation(calcID,transparency);
      else
        this.userSettingsService.setTransparencySubLayer(calcID,layers[0].name,transparency);

      layers.forEach((layer:Layer)=>{
        layer.olLayer.getSource().getFeatures().forEach((feature:Feature)=>
        {
          layer.olLayer.getSource().removeFeature(feature);
          if((feature.getStyle() as Style).getStroke != null && ((feature.getStyle() as Style).getStroke() != null)){
            let stroke:Stroke = (feature.getStyle() as Style).getStroke();
            this.utilsService.changeStrokeTransparency(stroke,transparency);
          }
          if((feature.getStyle() as Style).getFill != null && (feature.getStyle() as Style).getFill() != null){
            let fill:Fill = (feature.getStyle() as Style).getFill();
            this.utilsService.changeFillTransparency(fill,transparency);
          }
          else if((feature.getStyle() as Style).getImage != null && (feature.getStyle() as Style).getImage() != null){
            let fill:Fill = ((feature.getStyle() as Style).getImage() as CircleStyle).getFill();
            this.utilsService.changeFillTransparency(fill,transparency);
            feature.setStyle(new Style({
              image: new CircleStyle({
                radius: 3,
                fill: fill,
              })
            }))
          }
          layer.olLayer.getSource().addFeature(feature);
        }
        )
      })
    })
  }
  deleteCalculation(child: TopLevelLayers,deleteAll?:boolean){
    if (child.type == 'PROCEDURE_ANALYSIS'){
      child.deleteCalculation(child.type,() => this.tabsService.forceClosePerformanceAnalysisResultTab(child.calcID),deleteAll);
    } else {
      child.deleteCalculation(child.type,() => this.tabsService.forceCloseAnalysisTool(child.calcID),deleteAll);
    }

   // this.userSettingsService.deleteCalculationSettings(child.calcID);
  }
  deleteSelectedCalculationsClickEvent() : void {

    if (this.globalSelectionService.calculationLayers.childLayers.length > 0){
      this.modalConfirmation.showMesssageConfirmationModal("Delete Calculations", "Do you want to delete the selected Calculations?", "Yes", "No", () => this.deleteSelectedCalculations());
    }
    /**
     *
     this.userSettingsService.userSettings.calculationsVisibility = []
     this.userSettingsService.updateSettings();
     */
  }
  getCalculationLayers(){
    let layers: Layer[] = [];
    for(const childLayer of this.globalSelectionService.calculationLayers.childLayers){
      layers.push(...childLayer.layers)
    }
    return layers;
  }
  private deleteSelectedCalculations(){
    this.isDeleteAll = true;
    try {
      const calculationsCheckBoxes : NodeListOf<Element> = document.getElementsByName("a_calculation_checkbox");
      const calculationsDeleteIcons : NodeListOf<Element> = document.getElementsByName("a_calculation_delete_icon");
      let idsToDelete : Array<number> = [];
      for (let i=0; i<calculationsCheckBoxes.length; i++){
        if((calculationsCheckBoxes[i]as HTMLInputElement).checked){
          idsToDelete.push(+calculationsCheckBoxes[i].id);
        }
      }

      this.calculationsService.deleteBulKCalculation(idsToDelete).subscribe(
        (data) =>{
          idsToDelete.forEach( id =>{
            this.calculationsService.removeLayers(id);
          });
          this.userSettingsService.deleteCalculationsSettings(idsToDelete);
          this.modalService.showMesssageModal("Delete Calculations", "Calculations deleted with success.");
          this.uiBlock.stopBlock();
          this.tabsService.deleteCalculationTabs(idsToDelete);
        },
        (error) => {
          this.calculationsService.showError(error);
          this.uiBlock.stopBlock();
        }

      );;
    } catch (e){
      this.isDeleteAll = false;
    }
    this.isDeleteAll = false;

  }
  selectCalculation(child: TopLevelLayers,subLayers: boolean,docAndSectorization?:boolean){
    let calculationVisible=this.userSettingsService.userSettings.calculationsVisibility
    .find(elem => elem.id == child.calcID);
    if(subLayers){
      this.globalSelectionService.calculationLayers.onSelectPerformance(child);
      if (calculationVisible != null) {
        calculationVisible.visibleLayers.forEach(visibleLayer => {
          visibleLayer.visible = child.layers.find(layer => docAndSectorization ? layer.navaidid == visibleLayer.name : layer.name == visibleLayer.name).visible;
        })
      }
    }
    else {
      const visible = !child.allVisible();
      this.globalSelectionService.calculationLayers.onSelect(child);
      if (calculationVisible != null) {
        calculationVisible.visible = visible;
      }
    }
    this.userSettingsService.updateSettings();
  }
  selectSubLayer(calcID: number, layer: Layer){
    this.globalSelectionService.calculationLayers.onSelect(layer)
    this.userSettingsService.userSettings.calculationsVisibility
    .find(elem => elem.id == calcID)
    .visibleLayers
    .find(elem => elem.name == layer.name).visible = layer.visible;
    this.userSettingsService.updateSettings();
  }
  selectSubLayerDocSectorization( topLevelLayer:TopLevelLayers,navaidid:string){
    topLevelLayer.onSelectDocSectorization(navaidid);
    let visibleLayers = this.userSettingsService.userSettings.calculationsVisibility
    .find(elem => elem.id == topLevelLayer.calcID)
    .visibleLayers;
    topLevelLayer.layers.filter(layer => layer.navaidid == navaidid)
    .forEach((layer) =>
    {
      visibleLayers.filter(visibleLayer => visibleLayer.name == layer.navaidid)
      .forEach(visibleLayer => visibleLayer.visible = layer.visible);
    }
    );
    this.userSettingsService.updateSettings();
  }
  getDocAndSectorizationVisibility(layer:TopLevelLayers, navaidid: string){
    return layer.layers.find(layer => navaidid == layer.navaidid).visible;
  }
  selectAllCalculations(event){
    for (const child of this.globalSelectionService.horizonToolLayers.childLayers){
      this.globalSelectionService.horizonToolLayers.onSelect(child);
    }
    for (const child of this.globalSelectionService.calculationLayers.childLayers){
      if (child.type == "Performance" || child.type == 'PROCEDURE_ANALYSIS' ) {
        if (this.myXOR(child.oneVisible(), event.target.checked)){
          this.globalSelectionService.calculationLayers.onSelectPerformance(child);
        }
      } else {
        if (this.myXOR(child.allVisible(), event.target.checked)){
          this.globalSelectionService.calculationLayers.onSelect(child);
        }
      }
      let calculationVisibility = this.userSettingsService.userSettings.calculationsVisibility
          .find(visibleLayer => visibleLayer.id == child.calcID);
      if(calculationVisibility.visibleLayers != null){
        calculationVisibility.visibleLayers.forEach(visibleSubLayer => visibleSubLayer.visible = child.layers[0].visible);
    }
      else{
        calculationVisibility.visible = child.layers[0].visible;
  }
    }
    this.userSettingsService.updateSettings();
  }
  myXOR(a,b) {
    return ( a || b ) && !( a && b );
  }

  async renameCalculation(layer: TopLevelLayers){
    this.uiBlock.startBlock("Opening Rename Calculation window...");
    const calculation = await this.calculationsService.getCalculationById(layer.calcID);
    if (calculation == null){
      return;
    }
    this.uiBlock.stopBlock();
    const bsModalRef : BsModalRef = this.popupService.show(SingleTextInputModalComponent);
    bsModalRef.content.modalTitle = "Rename Calculation";
    bsModalRef.content.inputLabel = "Name";
    bsModalRef.content.isCalculationName = true;
    bsModalRef.content.inputText = calculation.calcName;
    bsModalRef.content.onClose.subscribe(async (calcNewName: string) =>{
      if (calcNewName && calcNewName.length > 0 && calculation.calcName != calcNewName){
        calculation.calcName = calcNewName;
        if (await this.calculationsService.updateCalculation(calculation)){
          this.calculationsService.removeLayers(layer.calcID);
          this.calculationsService.addCalculationToMap(calculation);
          this.tabsService.updateAnalysisToolTabsHeaders(calculation.id, layer.type, calcNewName);
        };
      }
    })
  }

  async openAnalysisTool(child: TopLevelLayers, layerName? : string){
    this.uiBlock.startBlock("Opening Analysis Tool window...");
    const calculation = await this.calculationsService.getCalculationById(child.calcID);
    if (calculation == null){
      return;
    }
    this.uiBlock.stopBlock();
    this.tabsService.openNewAnalysisTool(calculation.id, child.type, calculation.calcName, layerName);
  }

  async openCalculationConfiguration(calcId: number, calcType: string){
    this.uiBlock.startBlock("Opening Calculation Configurations window...");
    const calculation = await this.calculationsService.getCalculationById(calcId);
    if (calculation == null){
      return;
    }
    this.uiBlock.stopBlock();
    this.tabsService.openNewCalculationConfiguration(calcId, calcType, calculation.calcName);
  }

  isPerformanceAnalysisAvaible(calcID: number): boolean{
    return this.tabsService.tabs.findIndex((tab) => tab.type == TabsService.PERFORMANCE_ANALYSIS &&
                    this.tabsService.performanceAnalysisTabMatchesWithID(tab,calcID)) != -1;
  }

  getSortedProgressMap(items) {
    if (this.projectService.activeProject == null || this.projectService.activeProject.name == null){
      return items;
    }
    const activeProjectName = this.projectService.activeProject.name;
    return items.sort((a, b) => {
      if (a.value.projectName == null && b.value.projectName == null){
        return 0;
      }

      if (a.value.projectName != null && b.value.projectName != null){
        if (a.value.projectName == activeProjectName && b.value.projectName != activeProjectName) return -1;
        if (a.value.projectName != activeProjectName && b.value.projectName == activeProjectName) return 1;
      } else {
        if (a.value.projectName != null && b.value.projectName == null) return -1;
        if (a.value.projectName == null && b.value.projectName != null) return 1;
      }

      return 0;
    });
  }

  /**
   * Return true when the Cancel button should be shown for an ongoing calculation.
   * Hides the button for TERRAIN_HEIGHT_BULK calculations and handles null-safety.
   */
  showCancel(onGoingCalc: any): boolean {
    if (!onGoingCalc || !onGoingCalc.value) return false;
    const calcType = onGoingCalc.value.calculationType;
    return calcType !== 'TERRAIN_HEIGHT_BULK' && calcType !== 'TERRAIN_HEIGHT';
  }

}

